import React, { Fragment } from 'react';
import {
	Button,
	ButtonGroup,
	Checkbox,
	FormGroup,
	HalfIcon,
	Input,
	Select,
	SelectAdv,
	styled,
	Tooltip,
	WholeIcon,
} from '@lib/components';
import { FaArrowDown, FaArrowUp, FaCopy, FaMinusCircle } from 'react-icons/fa';
import { arrayMove } from 'react-sortable-hoc';
import { cloneDeepSafe } from '@lib/common';
import { inject, observer } from 'mobx-react';
import { MobxComponent } from '../../../../../../mobx/components';
import _ from 'lodash';
import { isMobile } from 'react-device-detect';
import { IconCopyAlt, IconDelete } from '../../../../../components/v2/icons';
import { MenuCardActionsIconWrapper } from '../../menus-v2';
import { SettingsFormInputContainer } from '../../settings-v2';
import uuid from 'uuid';

const autobind = require("class-autobind").default;
const IconWrapperComponent: React.FC<{ children: React.ReactNode }> | typeof Button = isMobile ? Button : React.Fragment
let wpos_status: boolean = false;//

type ItemType = T.Schema.Restaurant.Menu.RestaurantOptionSetOption;
type ListType = ItemType[];
type HandleChange = (
	i: number,
	key: string,
	value: any
) => void;
type HandleSetDefault = (i: number, quantity: 1 | 0) => void;
type HandleByIndex = (i: number) => void;
type HandleMove = (index: number, direction: 1 | -1) => void;

interface Props {
	values: T.Schema.Restaurant.Menu.RestaurantOptionSet;
	option_sets: T.Schema.Restaurant.Menu.RestaurantOptionSet[];
	setFieldValue: (key: string, value: any) => void;
	defaultEnabled?: boolean;
	restaurantStock: T.Schema.RestaurantMenuStock.Schema;
	setRestaurantStock: (stock: T.Schema.RestaurantMenuStock.Schema) => void;
	isModernVersion?: boolean;
  isFromMenuV2?: boolean;
}
interface TableDataProps {
	items: ListType;
	change: HandleChange;
	remove: HandleByIndex;
	copy: HandleByIndex;
	move: HandleMove;
	setDefault: HandleSetDefault;
	variablePriceOptionSet: T.Schema.Restaurant.Menu.RestaurantOptionSet | null;
	showVariablePrice: boolean;
	defaultEnabled?: boolean;
	restaurantStock: T.Schema.RestaurantMenuStock.Schema;
	setRestaurantStock: (stock: T.Schema.RestaurantMenuStock.Schema) => void;
	usingPoints?: boolean;
	selectedOptionId?: string;
	currencySymbol?: string;
	isModernVersion?: boolean;
  isFromMenuV2?: boolean;
}

const Table = styled.table`
  background: white;
  width: 100%;
  /* Without a floor, auto table layout shrinks numeric columns and steals width for text columns */
  min-width: 68rem;
  thead {
    th {
      white-space: nowrap;
    }
  }
  td {
    vertical-align: middle;
  }
  th.option-set-pos-id-col,
  td.option-set-pos-id-col {
    max-width: 6.5rem;
    width: 6.5rem;
    box-sizing: border-box;
  }
  td.option-set-pos-id-col .flex-center {
    min-width: 0;
  }
  td.option-set-pos-id-col input.no-round {
    min-width: 0;
    width: 100%;
    text-overflow: ellipsis;
  }
  @media (max-width: 767px) {
    min-width: 58rem;
  }
`;
const ActionButtonModernVersion = styled.div<{ active?: boolean }>`
	display: flex;
	padding: 12px 16px;
	justify-content: center;
	align-items: center;
	gap: 4px;
	border-radius: 4px;
	background: ${({ active }) => active ? '#FB2F2F' : "#F1F4F7"};
	color: ${({ active }) => active ? '#F1F4F7' : "#4B4F5E"};
	text-align: center;
	font-family: Inter;
	font-size: 14px;
	font-style: normal;
	font-weight: 600;
	line-height: 16px;
	width: 140px;
	height: 40px;
`;
const TableWrapperOptionSet = styled.div<{ isModern?: boolean }>`
	height: ${({ isModern }) => isModern ? '56vh' : '30vh'};
	overflow: auto;
	@media (max-width: 767px) {
        height: ${({ isModern }) => isModern ? '63vh' : '30vh'};
    }

`
const PlusIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M7.33333 11.3333H8.66667V8.66667H11.3333V7.33333H8.66667V4.66667H7.33333V7.33333H4.66667V8.66667H7.33333V11.3333ZM2 14V2H14V14H2ZM3.33333 12.6667H12.6667V3.33333H3.33333V12.6667Z" fill="#4B4F5E" />
	</svg>
)
const rowDataProps = { className: "border-white-20 p-lr-1 p-tb-2" };

const isPointPrices = (price: T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices, pointType: string) =>
	typeof price === "object" && price !== null && pointType in price;

/** Menu v2 modifiers use `id`; legacy option sets and options use `_id`. */
function entityId(e: { id?: string; _id?: string } | null | undefined): string {
	if (!e) return "";
	return String((e as { id?: string; _id?: string }).id ?? (e as { id?: string; _id?: string })._id ?? "");
}

/**
 * For menu v2, the working price for inputs is usually `price.value` (same as before OptionPricing fallbacks).
 * Also support API-only fields pointValue / standardValue when `value` is absent.
 */
function getTableItemPrice(
	item: ItemType,
	isFromMenuV2: boolean | undefined
): T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices | undefined {
	if (!isFromMenuV2) {
		return item.price;
	}
	const raw = item.price as unknown as Record<string, unknown> | null | undefined;
	if (raw == null || typeof raw !== "object") {
		return item.price;
	}
	if (raw.value !== undefined && raw.value !== null) {
		return raw.value as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices;
	}
	if (raw.pointValue != null) {
		return raw.pointValue as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices;
	}
	if (raw.standardValue !== undefined && raw.standardValue !== null) {
		return raw.standardValue as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices;
	}
	// Some payloads use v1-shaped `price: { whole, half }` without a `value` envelope.
	if (
		typeof raw.whole === "object" &&
		raw.whole !== null &&
		typeof raw.half === "object" &&
		raw.half !== null
	) {
		return raw as unknown as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices;
	}
	return undefined;
}

type PointSliceKey = "points" | "single" | "double";

/** Input `value=` for point cells: do not use `x ? y` — numeric 0 is valid. */
function getPointSliceField(
	itemPrice: T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices | undefined,
	section: "whole" | "half",
	field: PointSliceKey
): string | number {
	if (itemPrice === undefined || itemPrice === null) return "";
	if (typeof itemPrice !== "object" || Array.isArray(itemPrice)) return "";
	const slice = (itemPrice as unknown as Record<string, { [k in PointSliceKey]?: number } | undefined>)[section];
	if (!slice || typeof slice !== "object") return "";
	const v = slice[field];
	return v === undefined || v === null ? "" : v;
}

function getMenuV2PointBodyForAutoCalc(price: ItemType["price"] | null | undefined): { whole?: any; half?: any } | undefined {
	if (!price || typeof price !== "object") return undefined;
	const p = price as unknown as Record<string, unknown>;
	const fromVal = p.value;
	if (fromVal && typeof fromVal === "object" && fromVal !== null && "whole" in (fromVal as object)) {
		return fromVal as { whole?: any; half?: any };
	}
	const fromPv = p.pointValue;
	if (fromPv && typeof fromPv === "object" && fromPv !== null && "whole" in (fromPv as object)) {
		return fromPv as { whole?: any; half?: any };
	}
	if ("whole" in p && (p as any).whole) {
		return p as { whole?: any; half?: any };
	}
	return undefined;
}

@observer
class TableData extends React.Component<TableDataProps> {
	render() {
		const {
			items,
			change,
			remove,
			copy,
			move,
			setDefault,
			defaultEnabled,
			variablePriceOptionSet,
			showVariablePrice,
			restaurantStock,
			setRestaurantStock,
			usingPoints,
			selectedOptionId,
			currencySymbol,
			isModernVersion,
      isFromMenuV2
		} = this.props;
		return (
			<>
				{(items || []).map((item, i) => {
					const rowId = entityId(item);
					const stock =
						typeof restaurantStock.option_set_options[rowId] === "number"
							? restaurantStock.option_set_options[rowId]
							: "";
					const itemPrice = getTableItemPrice(item, isFromMenuV2);
					return (
						<tr key={`trow-${rowId}-${i}`} className="bg-white border-white-20">
							{(!isMobile && !isModernVersion) && <>
								<td
									className="border-white-20 cursor text-center"
									onClick={() => move(i, -1)}
								>
									<Tooltip
										text="Move Up"
										width={65}
										position="top"
										disable={i !== 0}
									>
										<FaArrowUp />
									</Tooltip>
								</td>

								<td
									className="border-white-20 cursor text-center"
									onClick={() => move(i, 1)}
								>
									<Tooltip
										text="Move Down"
										width={85}
										position="top"
										disable={i !== 0}
									>
										<FaArrowDown />
									</Tooltip>
								</td>
							</>}

							<td className="border-white-20">
								<div className="flex-center">
									<Input
										type="text"
										className="no-round no-border"
										value={item.name}
										placeholder={"Name"}
										required={true}
										onChange={(e: any) => change(i, "name", e.target.value)}
									/>
								</div>
							</td>
							<td className="border-white-20">
								<div className="flex-center">
									<Input
										type="text"
										className="no-round no-border"
										value={item.print_name || ''}
										placeholder={"Print Name"}
										onChange={(e: any) => change(i, "print_name", e.target.value)}
									/>
								</div>
							</td>
							{!showVariablePrice && !usingPoints && (
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										step={0.01}
										className="no-round no-border"
										placeholder={currencySymbol}
										// @ts-ignore
										value={(itemPrice ?? "") as string | number}
										onWheel={(e) => e.currentTarget.blur()}
										onChange={(e: any) =>
											change(i, isFromMenuV2 ? "price.value" : "price", e.target.value)
										}
									/>
								</td>
							)}

							{!showVariablePrice && usingPoints && (<>
								<td className="border-white-20">
									<Input
										type="number"
										min={0.5}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={getPointSliceField(itemPrice, "whole", "points")}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													points: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={getPointSliceField(itemPrice, "whole", "single")}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													single: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										className="no-round no-border"
										onWheel={(e) => e.currentTarget.blur()}
										placeholder={currencySymbol}
										value={getPointSliceField(itemPrice, "whole", "double")}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													double: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0.5}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={""}
										value={getPointSliceField(itemPrice, "half", "points")}
										onChange={(e: any) => {
											const newValue = {
												half: {
													points: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={getPointSliceField(itemPrice, "half", "single")}
										onChange={(e: any) => {
											const newValue = {
												half: {
													single: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={getPointSliceField(itemPrice, "half", "double")}
										onChange={(e: any) => {
											const newValue = {
												half: {
													double: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											const priceVal = _.cloneDeep(itemPrice) || {};
											_.merge(priceVal, newValue);
											const key = isFromMenuV2 ? "price.value" : "price";
											change(i, key, priceVal);
										}}
									/>
								</td>
							</>)}

							{showVariablePrice && !usingPoints &&
								variablePriceOptionSet!.options.map((o) => {
									const colId = entityId(o);
									return (
									<td key={`no-point-${colId}-tdata`} className="border-white-20">
										<Input
											type="number"
											min={0}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={(item.prices && item.prices[colId] ? item.prices[colId] : "") as string}
											onChange={(e: any) => {
												const variable_prices = item.prices || {};
												variable_prices[colId] = e.target.value;
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
								);})}

							{showVariablePrice && usingPoints && variablePriceOptionSet && variablePriceOptionSet.options &&
								variablePriceOptionSet!.options.map((o, j) => {
									const colId = entityId(o);
									return (<Fragment key={`with-point-${colId}-tdata`}>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0.5}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={""}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"whole",
												"points"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														whole: {
															points: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"whole",
												"single"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														whole: {
															single: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"whole",
												"double"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														whole: {
															double: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0.5}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={""}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"half",
												"points"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														half: {
															points: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"half",
												"single"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														half: {
															single: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={colId === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={colId === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={getPointSliceField(
												item.prices && typeof item.prices[colId] === "object"
													? (item.prices[colId] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices)
													: undefined,
												"half",
												"double"
											)}
											onChange={(e: any) => {
												const newValue = {
													[colId]: {
														half: {
															double: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
								</Fragment>);
								})}

							<td className="border-white-20" style={{ display: "none" }}>
								<Input
									type="number"
									min={0}
									step={1}
									className="no-round no-border"
									placeholder={""}
									value={item.status === "not-available" ? "" : stock}
									disabled={item.status === "not-available"}
									onChange={(e: any) => {
										let value: "" | number;
										try {
											value =
												e.target.value === "" ? "" : parseInt(e.target.value);
										} catch (e) {
											console.log(e);
											return;
										}

										const rs = cloneDeepSafe(restaurantStock);

										if (value === "") {
											delete rs.option_set_options[rowId];
										} else {
											rs.option_set_options[rowId] = value;
										}

										setRestaurantStock(rs);
									}}
								/>
							</td>

							<td className="border-white-20 p-lr-2">
								<Checkbox
									id={`${i}-unavailable`}
									checked={item.status === "not-available"}
									onChange={() => {
										change(
											i,
											"status",
											item.status === "not-available" ? null : "not-available"
										);
										if (item.quantity === 1) {
											setDefault(i, 0);
										}
									}}
								/>
							</td>

							{defaultEnabled && (
								<td className="border-white-20 p-lr-2">
									<Checkbox
										id={`${i}-defaultos`}
										checked={item.quantity === 1}
										onChange={() => setDefault(i, item.quantity === 1 ? 0 : 1)}
									/>
								</td>
							)}

							{wpos_status && (
								<td className="border-white-20 option-set-pos-id-col">
									<div className="flex-center">
										<Input
											type="number"
											className="no-round no-border"
											value={item.integrations?.pushpos?.item_id || ''} // Safely access item_id
											placeholder={"POS Item Id"}
											required={false}
											onChange={(e: any) => {
												const updatedValue = parseInt(e.target.value, 10);

												// Ensure pushpos exists before updating
												const updatedIntegrations = {
													...item.integrations,
													pushpos: {
														...item.integrations?.pushpos,
														item_id: updatedValue,
													},
												};

												change(i, "integrations", updatedIntegrations); // Pass the updated integrations object
											}}
										/>
									</div>
								</td>
							)}

							{!isModernVersion && <><td
								className="border-white-20 cursor text-center"
								onClick={() => remove(i)}
							>
								<Tooltip
									text="Delete"
									width={65}
									position="top"
								>
									<IconWrapperComponent>
										<FaMinusCircle />
									</IconWrapperComponent>
								</Tooltip>
							</td>
								<td
									className={"border-white-20 cursor text-center p-lr-1"}
									onClick={() => copy(i)}
								>
									<Tooltip
										text="Copy"
										width={55}
										position="top"
									>
										<IconWrapperComponent>
											<FaCopy />
										</IconWrapperComponent>
									</Tooltip>
								</td></>}

							{isModernVersion && <>
								<td
									className="border-white-20 cursor text-center"
									style={{ display: 'flex', gap: '8px', padding: '8px' }}
								>
									<Tooltip
										text="Move Up"
										width={65}
										position="top"
										disable={i !== 0}
									>
										<MenuCardActionsIconWrapper onClick={() => move(i, -1)}>
											<FaArrowUp />
										</MenuCardActionsIconWrapper>
									</Tooltip>
									<Tooltip
										text="Move Down"
										width={85}
										position="top"
										disable={i !== 0}
									>
										<MenuCardActionsIconWrapper onClick={() => move(i, 1)}>
											<FaArrowDown />
										</MenuCardActionsIconWrapper>
									</Tooltip>
									<Tooltip
										text="Delete"
										width={65}
										position="top"
									>
										<MenuCardActionsIconWrapper onClick={() => remove(i)}>
											<IconDelete />
										</MenuCardActionsIconWrapper>
									</Tooltip>
									<Tooltip
										text="Copy"
										width={55}
										position="top"
									>
										<MenuCardActionsIconWrapper onClick={() => copy(i)}>
											<IconCopyAlt />
										</MenuCardActionsIconWrapper>
									</Tooltip>

								</td>
							</>}

						</tr>
					);
				})}
			</>
		);
	}
}

@inject("store")
@observer
export class FieldOptionSetOptions extends MobxComponent<
	Props,
	{
		priceView: string;
		selectedOptionId: string;
	}
> {
	constructor(props: Props) {
		super(props);
		autobind(this);
		// @ts-ignore — v2 modifiers: `type`; legacy: `using_points`
		const { using_points, type } = props.values;
		const isUsingPoints = using_points || type === "pizza";
		const showVariablePriceViewToggle = isUsingPoints || !!props.isFromMenuV2;
		this.state = {
			priceView:
				props.values.variable_price_ref && showVariablePriceViewToggle
					? "variable"
					: "standard",
			selectedOptionId: this.getFirstVariableOptionSetOptionId(props)
		};
	}

	remove: HandleByIndex = (i) => {
		const values = [...this.props.values.options];
		values.splice(i, 1);
		this.props.setFieldValue("options", values);
	};

	copy: HandleByIndex = (i) => {
		const values = cloneDeepSafe(this.props.values.options);

		const newValue = {
			...JSON.parse(JSON.stringify(values[i])),
			_id: "",
			name: "",
			quantity: 0,
		};

		values.splice(i + 1, 0, newValue);
		this.props.setFieldValue("options", values);
	}

	change: HandleChange = (i, key, value) => {
		const values = cloneDeepSafe(this.props.values.options);
		_.set(values[i], key, value);
		this.props.setFieldValue("options", values);
	};
	move: HandleMove = (index, direction) => {
		const values = [...this.props.values.options];

		if (
			(index === 0 && direction === -1) ||
			(index === values.length - 1 && direction === 1)
		) {
			return;
		}

		this.props.setFieldValue(
			"options",
			arrayMove(values, index, index + direction)
		);
	};
	calculateWidth(options: any) {
		let width;
		options.map((option: any) => {
			if (option.name.length < 10) {
				width = 120;
			} else if (option.name.length > 10 && option.name.length < 15) {
				width = 160;
			} else {
				width = 200;
			}
			return width;
		});
	}
	setDefault: HandleSetDefault = (i, quantity) => {
		this.props.setFieldValue(
			"options",
			this.props.values.options.map((item, index) => ({
				...item,
				quantity: i === index ? quantity : 0,
				status: i === index ? null : item.status,
			}))
		);
	};

	componentDidUpdate(prevProps: Readonly<Props>): void {
		if (
			this.props.values.variable_price_ref !==
			prevProps.values.variable_price_ref
		) {
			if (this.props.values.variable_price_ref) {
				const values = cloneDeepSafe(this.props.values.options);
				for (let i = 0; i < values.length; i++) {
					values[i].prices = {};
				}
				this.props.setFieldValue("options", values);
			}
			this.setState({
				selectedOptionId: this.getFirstVariableOptionSetOptionId(this.props),
			});
		}
	}

	getFirstVariableOptionSetOptionId(props: Readonly<Props>): string {
		const ref = props.values.variable_price_ref;
		const variablePriceOptionSet = !ref ? null : props.option_sets.find((os) => entityId(os) === ref) || null;
		const opts = variablePriceOptionSet && (variablePriceOptionSet as { options?: unknown[] }).options;
		return opts && opts.length > 0 ? entityId(opts[0] as { id?: string; _id?: string }) : "";
	}

	formatDecimalPlaces(value: number) {
		return value == 0 ? value : Number(value.toFixed(2))
	}

	autoCalculate(): void {
		// get all option set options (#main, #variable);
		// loop main and using whole points and whole single values, autocalculate the rest
		// subloop #variable and do the same
		// NOTE: I can't do type checking here :(
		const { priceView } = this.state;
		const { values, option_sets, setFieldValue, isFromMenuV2 } = this.props;
		// @ts-ignore
    const { variable_price_ref, using_points, type } = values;
    const isUsingPoints = using_points || type === "pizza";

		const variablePriceOptionSet = !variable_price_ref ? null : option_sets.find((os) => entityId(os) === variable_price_ref) || null;
		const showVariablePrice = priceView === "variable" && !!variablePriceOptionSet;

		if (isUsingPoints && values.options) {
			const optionSetOptions = cloneDeepSafe(values.options);
			const newOptionSetOptions = optionSetOptions.map((option) => {
				const v2PointBody = isFromMenuV2 ? getMenuV2PointBodyForAutoCalc(option.price) : undefined;
				const wholePriceObject = isFromMenuV2
					? v2PointBody && v2PointBody.whole
						? v2PointBody.whole
						: undefined
					: typeof option.price === "object" && option.price && "whole" in option.price
						? (option.price as { whole?: any }).whole
						: undefined;
				if (!showVariablePrice && option.price) {
					console.log(`AUTO CALCULATING STANDARD PRICING`);
					// calculate points
					const whole_points =
						wholePriceObject != null && wholePriceObject.points !== undefined && wholePriceObject.points !== null
							? Number(wholePriceObject.points)
							: 0;
					const half_points = whole_points / 2;
					// calculate prices
					const whole_single_value = this.formatDecimalPlaces(
						wholePriceObject != null &&
							wholePriceObject.single !== undefined &&
							wholePriceObject.single !== null
							? Number(wholePriceObject.single)
							: 0
					);
					const whole_double_value = this.formatDecimalPlaces(whole_single_value * 2);
					const half_single_value = this.formatDecimalPlaces(whole_single_value / 2);
					const half_double_value = this.formatDecimalPlaces(whole_single_value);

					const newOptionValue = {
						whole: {
							points: whole_points,
							single: whole_single_value,
							double: whole_double_value
						},
						half: {
							points: half_points,
							single: half_single_value,
							double: half_double_value
						}
					};
					if (isFromMenuV2) {
						const p =
							option.price && typeof option.price === "object"
								? (option.price as unknown as Record<string, unknown>)
								: {};
						option.price = {
							...p,
							type: (p.type as string) || "point",
							value: newOptionValue,
							pointValue: newOptionValue,
						} as unknown as ItemType["price"];
					} else {
						option.price = {
							...(typeof option.price === 'object' && option.price !== null ? option.price : {}),
							...newOptionValue,
						} as ItemType['price'];
					}
				}
        else if (
					showVariablePrice &&
					variablePriceOptionSet &&
					variablePriceOptionSet.options &&
					variablePriceOptionSet.options.length > 0
				) {
					console.log(`AUTO CALCULATING VARIABLE PRICING`);
					const newOptionPrices = { ...(option.prices || {}) };
					const whole_points =
						wholePriceObject != null && wholePriceObject.points !== undefined && wholePriceObject.points !== null
							? Number(wholePriceObject.points)
							: 0;
					const half_points = whole_points / 2;
					const whole_single_value = this.formatDecimalPlaces(
						wholePriceObject != null &&
							wholePriceObject.single !== undefined &&
							wholePriceObject.single !== null
							? Number(wholePriceObject.single)
							: 0
					);
					const whole_double_value = this.formatDecimalPlaces(whole_single_value * 2);
					const half_single_value = this.formatDecimalPlaces(whole_single_value / 2);
					const half_double_value = this.formatDecimalPlaces(whole_single_value);
					const newOptionValue = {
						whole: {
							points: whole_points,
							single: whole_single_value,
							double: whole_double_value
						},
						half: {
							points: half_points,
							single: half_single_value,
							double: half_double_value
						}
					};
					for (const vo of variablePriceOptionSet.options as T.Schema.Restaurant.Menu.RestaurantOptionSetOption[]) {
						newOptionPrices[entityId(vo)] = newOptionValue;
					}
					option.prices = newOptionPrices;
				}
				return option;
			});
			setFieldValue("options", newOptionSetOptions);
		}
	}

	render() {
		if (!this.state) return null
		const { priceView, selectedOptionId } = this.state;
		const { values, defaultEnabled, option_sets, setFieldValue } = this.props;
		// @ts-ignore
    const { variable_price_ref, using_points, type } = values;
    const isUsingPoints = using_points || type === "pizza";
		const showVariablePriceViewToggle = isUsingPoints || !!this.props.isFromMenuV2;

		const currencySymbol = this.injected.store.restaurant?.settings.region.currency.symbol || "$";

		const variablePriceOptionSet = !variable_price_ref ? null : option_sets.find((os) => entityId(os) === variable_price_ref) || null;
		const showVariablePrice = priceView === "variable" && !!variablePriceOptionSet;

		wpos_status = !!this.injected.store.restaurant?.wpos?.wpos_status;

		const sortVariablePriceOptions = (options: T.Schema.Restaurant.Menu.RestaurantOptionSetOption[]) => {
			const typeName = [
				options.filter((os) => os.name.length < 14),
				options.filter((os) => os.name.length >= 14 && os.name.length < 23),
				options.filter((os) => os.name.length >= 23)
			];

			const sortedOptions = [];

			typeName[2].forEach((item) => sortedOptions.push([item]));

			while (typeName[1].length > 0) {
				const group = [typeName[1].shift()];
				if (typeName[1].length > 0) group.push(typeName[1].shift());
				sortedOptions.push(group);
			}

			while (typeName[0].length >= 3) {
				sortedOptions.push([typeName[0].shift(), typeName[0].shift(), typeName[0].shift()]);
			}

			while (typeName[0].length > 0) {
				const group = [typeName[0].shift()];
				if (typeName[0].length > 0) group.push(typeName[0].shift());
				sortedOptions.push(group);
			}

			return sortedOptions.flat();
		};


		return (
			<>
				{!this.props.isModernVersion &&
					<div className="flex-line flex-center-v flex-wrap m-b-3">

						{isMobile ? <FormGroup
							className="flex-grow"
							title="Name"
							optional
							help="Used to accurately calculate store timings">
							<SelectAdv
								placeholder=''
								type="single"
								value={values.variable_price_ref ?? null}
								onChange={(
									option: string
								) => {
									if (option === "") {
										this.setState({ priceView: "standard" });
									}
									setFieldValue("variable_price_ref", option);
								}}
								options={[
									{ value: "", label: "No Variable Price 1" },
									...option_sets
										.filter((os) => {
											const c = os.conditions as { multi_select?: boolean; quantity_select?: boolean } | undefined;
											return (
												entityId(os) !== entityId(values) &&
												c &&
												!c.multi_select &&
												!c.quantity_select
											);
										})
										.map((os) => ({ value: entityId(os), label: os.name })),
								]}
							/>
						</FormGroup> :
							<Select
								style={{ width: "auto", height: "30px" }}
								className="flex-grow"
								value={values.variable_price_ref}
								onChange={(e) => {
									if (e.target.value === "") {
										this.setState({ priceView: "standard" });
									}
									setFieldValue("variable_price_ref", e.target.value);
								}}
								options={[
									{ value: "", label: "No Variable Price 2" },
									...option_sets
										.filter((os) => {
											const c = os.conditions as { multi_select?: boolean; quantity_select?: boolean } | undefined;
											return (
												entityId(os) !== entityId(values) &&
												c &&
												!c.multi_select &&
												!c.quantity_select
											);
										})
										.map((os) => ({ value: entityId(os), label: os.name })),
								]}
							/>
						}


						{values.variable_price_ref && (!isMobile ?
							<ButtonGroup
								size="xs"
								width={100}
								className="m-l-1"
								selected={priceView}
								onSelect={(v) => this.setState({ priceView: v.value as string })}
								options={[
									{ value: "standard", name: `Regular (${currencySymbol})` },
									{ value: "variable", name: `Variable (${currencySymbol})` },
								]}
							/> : (
								<div style={(isMobile || this.props.isModernVersion) ? { display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%' } : { width: '100%' }}>
									<Button
										full={isMobile}
										size={isMobile ? "sm" : "xs"}
										type="button"
										color={isMobile ? undefined : "primary-inverse"}
										className="m-r-1"
										onClick={() => this.setState({ priceView: 'standard' })}
									>
										Regular ({currencySymbol})
									</Button>
									{showVariablePriceViewToggle && (
										<Button
											full={isMobile}
											size={isMobile ? "sm" : "xs"}
											type="button"
											color={isMobile ? undefined : "primary-inverse"}
											className="m-l-1"
											onClick={() => this.setState({ priceView: 'variable' })}
										>
											Variable ({currencySymbol})
										</Button>
									)}
								</div>
							)
						)}

					</div>
				}

				{!this.props.isModernVersion && <div className="flex-line flex-center-v flex-center-h flex-wrap m-b-3">
					<Button
						size="xs"
						type="button"
						color="primary-inverse"
						className="m-r-1"
						onClick={() => {
							setFieldValue("options", [
								...values.options,
								{
									_id: "",
									name: "",
									price: "",
									quantity: 0,
									prices: {},
								} as T.Schema.Restaurant.Menu.RestaurantOptionSetOption,
							]);
						}}
					>
						Add Option
					</Button>

					{isUsingPoints && (
						<Button
							size="xs"
							type="button"
							color="primary-inverse"
							className="m-l-1"
							onClick={() => { this.autoCalculate() }}
						>
							Auto Calculate
						</Button>
					)}
				</div>}


				{
					this.props.isModernVersion &&
					(
						<>
							<div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
								<SettingsFormInputContainer style={{ width: '100%', minHeight: '40px', flex: '0 0 40%', overflow: 'visible' }}>
									<SelectAdv
										placeholder=''
										type="single"
										value={values.variable_price_ref ?? null}
										onChange={(
											option: string
										) => {
											if (option === "") {
												this.setState({ priceView: "standard" });
											}
											setFieldValue("variable_price_ref", option);
										}}
										options={[
											{ value: "", label: "No Variable Price" },
											...option_sets
												.filter((os) => {
													const c = os.conditions as { multi_select?: boolean; quantity_select?: boolean } | undefined;
													return (
														entityId(os) !== entityId(values) &&
														c &&
														!c.multi_select &&
														!c.quantity_select
													);
												})
												.map((os) => ({ value: entityId(os), label: os.name })),
										]} />
								</SettingsFormInputContainer>


								{values.variable_price_ref && (
									<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
										<ActionButtonModernVersion
											active={this.state.priceView === 'standard'}
											onClick={() => this.setState({ priceView: 'standard' })}
										>
											Regular ({currencySymbol})
										</ActionButtonModernVersion>
										{showVariablePriceViewToggle && (
											<ActionButtonModernVersion
												active={this.state.priceView === 'variable'}
												onClick={() => this.setState({ priceView: 'variable' })}
											>
												Variable ({currencySymbol})
											</ActionButtonModernVersion>
										)}
									</div>
								)}
							</div>

							<div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
								<ActionButtonModernVersion
									style={{ flex: 1}}
									onClick={() => {
									setFieldValue("options", [
										...values.options,
										{
											_id: "",
											name: "",
											price: "",
											quantity: 0,
											prices: {},
										} as T.Schema.Restaurant.Menu.RestaurantOptionSetOption,
									]);
								}}>
									<PlusIcon />
									<div>Add Option</div>
								</ActionButtonModernVersion>
								{isUsingPoints && (
									<ActionButtonModernVersion
										style={{ flex: 1}}
										className="m-l-1"
										onClick={() => { this.autoCalculate(); }}
									>
										Auto Calculate
									</ActionButtonModernVersion>
								)}
							</div></>

					)
				}

				<div className="flex-line flex-center-v flex-center-h flex-wrap m-b-3">
					{values.variable_price_ref && priceView === "variable" && variablePriceOptionSet && variablePriceOptionSet.options && isUsingPoints && (
						<ButtonGroup
							size="sm"
							width={'auto'}
							className="m-l-1"
							buttonClassName="p-lr-2"
							selected={selectedOptionId}
							onSelect={(v) => this.setState({ selectedOptionId: v.value as string })}
							options={
								variablePriceOptionSet && sortVariablePriceOptions(variablePriceOptionSet.options as T.Schema.Restaurant.Menu.RestaurantOptionSetOption[])
									.map((os: any) => ({ value: entityId(os), name: os.name }))
							}
							buttonWidth
						/>
					)}
				</div>
				<TableWrapperOptionSet isModern={this.props.isModernVersion}>
					<Table className="width100" style={isMobile ? { display: 'block', overflowX: 'auto' } : {}}>
						<thead>
							<tr className="text-center">
								{(!isMobile && !this.props.isModernVersion) && <th
									{...rowDataProps}
									style={{ minWidth: "20px" }}
									colSpan={2}
								/>}
								<th {...rowDataProps}>Name</th>
								<th {...rowDataProps}>Print Name</th>
								{!showVariablePrice && !isUsingPoints && <th {...rowDataProps}>Price ({currencySymbol})</th>}
								{showVariablePrice && !isUsingPoints &&
									variablePriceOptionSet!.options.map((o, i) => (
										<th
											key={`no-points-theader-${entityId(o)}`}
											{...rowDataProps}
											style={this.props.isModernVersion ? {} : { whiteSpace: "normal" }}
										>
											{o.name} ({currencySymbol})
										</th>
									))}

								{!showVariablePrice && isUsingPoints && (<>
									<th {...rowDataProps}><WholeIcon /> Points</th>
									<th {...rowDataProps}><WholeIcon /> x1</th>
									<th {...rowDataProps}><WholeIcon /> x2</th>
									<th {...rowDataProps}><HalfIcon /> Points</th>
									<th {...rowDataProps}><HalfIcon /> x1</th>
									<th {...rowDataProps}><HalfIcon /> x2</th>
								</>)}

								{showVariablePrice && isUsingPoints &&
									variablePriceOptionSet!.options.map((o, i) => {
										const colId = entityId(o);
										return (<Fragment key={`with-points-theader-${colId}-${i}`}>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> Points</th>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> x1</th>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> x2</th>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> Points</th>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> x1</th>
										<th {...rowDataProps} style={colId === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> x2</th>
									</Fragment>
									);})}
								<th {...rowDataProps} style={{ display: "none" }}># in Stock</th>
								<th {...rowDataProps} >No Stock</th>
								{defaultEnabled && <th {...rowDataProps} style={{ maxWidth: "60px" }}>Default</th>}
								{wpos_status && <th {...rowDataProps} className={`${rowDataProps.className} option-set-pos-id-col`}>POS Item Id</th>}
								<th {...rowDataProps} style={{ minWidth: "20px" }} />
								{!this.props.isModernVersion && <th {...rowDataProps} style={{ minWidth: "20px" }} />}
							</tr>
						</thead>
						<tbody>
							<TableData
								items={values.options}
								change={this.change}
								remove={this.remove}
								copy={this.copy}
								move={this.move}
								setDefault={this.setDefault}
								defaultEnabled={defaultEnabled}
								showVariablePrice={showVariablePrice}
								variablePriceOptionSet={variablePriceOptionSet}
								restaurantStock={this.props.restaurantStock}
								setRestaurantStock={this.props.setRestaurantStock}
								usingPoints={isUsingPoints}
								selectedOptionId={selectedOptionId}
								currencySymbol={currencySymbol}
								isModernVersion={this.props.isModernVersion}
                isFromMenuV2={this.props.isFromMenuV2}
							/>
						</tbody>
					</Table>
				</TableWrapperOptionSet>
			</>
		);
	}
}
